import { UNASSIGNED_CALENDAR_EVENT } from "@/features/calendar/constants";
import { type CoWorkerCalendarItem } from "@/features/calendar/types";
import { isOffHour } from "@/features/calendar/utils";
import {
  getScheduleActivities,
  mapScheduleToActivity,
} from "@/features/calendar/work-schedule/getScheduleActivities";
import { getWorkhours } from "@/features/calendar/work-schedule/getWorkHours";
import { type Schedule, type Team } from "@repo/api";
import { areIntervalsOverlapping, isSameDay } from "date-fns";

import { sortMeetings } from "../utils/utils";
import { type CoWorkerCalendar } from "./types";

const getRowIndex = (
  meetingMatrix: CoWorkerCalendarItem[][],
  newItem: CoWorkerCalendarItem
) => {
  let index = 0;

  while (index < meetingMatrix.length || !meetingMatrix[index]) {
    const currentRow = meetingMatrix[index] ?? [];

    if (
      currentRow.findIndex((r) => {
        return (
          areIntervalsOverlapping(
            { start: r.start, end: r.end },
            { start: newItem.start, end: newItem.end }
          ) ||
          r.allDay ||
          newItem.allDay
        );
      }) === -1
    ) {
      return index;
    }

    index++;
  }

  return index;
};

export const groupEvents = ({
  items,
  schedules,
  workHoursSchedules,
  teams,
  startHour,
  endHour,
  selectedEmailId,
  selectedTeam,
  selectedDate,
  enableWorkhours,
}: {
  items: CoWorkerCalendarItem[];
  schedules: Schedule[] | undefined;
  workHoursSchedules?: Schedule[] | undefined;

  teams: Team[];
  startHour: number;
  endHour: number;
  selectedDate: Date;
  selectedEmailId?: string[];
  selectedTeam?: number;
  enableWorkhours?: boolean;
}) => {
  const sourceSchedules = schedules ?? [];
  const workHoursSource = workHoursSchedules ?? sourceSchedules;

  let memberEmails;

  if (selectedEmailId) {
    memberEmails = selectedEmailId;
  } else {
    memberEmails = teams
      .filter((team) => team.teamId === selectedTeam)
      .flatMap((team) => team.teamMembers.map((m) => m.emailId));
  }

  const members: [string, CoWorkerCalendar][] = [
    ...memberEmails.map((e) => [
      e,
      {
        emailId: e,
        meetings: [],
        offHourMeetings: [],
        workHours: getWorkhours(workHoursSource, e, enableWorkhours),
        activities: getScheduleActivities(sourceSchedules, e, enableWorkhours),
      },
    ]),
    [
      UNASSIGNED_CALENDAR_EVENT,
      {
        emailId: UNASSIGNED_CALENDAR_EVENT,
        meetings: [],
        offHourMeetings: [],
        workHours: getWorkhours(
          workHoursSource,
          UNASSIGNED_CALENDAR_EVENT,
          false
        ),
        activities: [],
      },
    ],
  ] as [string, CoWorkerCalendar][];

  let groupedItems = groupCalendarItems(
    items,
    sourceSchedules,
    enableWorkhours,
    startHour,
    endHour,
    members,
    selectedDate
  );

  groupedItems = groupScheduleActivities(
    groupedItems,
    sourceSchedules,
    selectedDate
  );

  const result = Array.from(groupedItems, (entry) => entry[1])
    .sort((a, b) => a.emailId.localeCompare(b.emailId))
    .sort(sortMeetings);

  return {
    coWorkers: result.filter((cw) => cw.emailId !== UNASSIGNED_CALENDAR_EVENT),
    unassigned: result.filter((cw) => cw.emailId === UNASSIGNED_CALENDAR_EVENT),
  };
};

function groupScheduleActivities(
  groupedItems: Map<string, CoWorkerCalendar>,
  schedules: Schedule[] | undefined,
  selectedDate: Date
) {
  return (schedules ?? []).reduce((memo, schedule) => {
    const activity = mapScheduleToActivity(schedule);

    const coWorkerCalendar = memo.get(activity.emailId);
    if (!coWorkerCalendar) {
      return memo;
    }

    if (
      !isSameDay(activity.start, selectedDate) &&
      !isSameDay(activity.end, selectedDate)
    ) {
      coWorkerCalendar.offHourMeetings.push(activity);
    } else {
      const rowIndex = getRowIndex(coWorkerCalendar.meetings, activity);

      if (!coWorkerCalendar.meetings[rowIndex]) {
        coWorkerCalendar.meetings[rowIndex] = [];
      }
      coWorkerCalendar.meetings[rowIndex].push(activity);
    }

    memo.set(activity.emailId, coWorkerCalendar);

    return memo;
  }, groupedItems);
}

function groupCalendarItems(
  items: CoWorkerCalendarItem[],
  schedules: Schedule[] | undefined,
  enableWorkhours: boolean | undefined,
  startHour: number,
  endHour: number,
  members: [string, CoWorkerCalendar][],
  selectedDate: Date
) {
  const memberMap = new Map<string, CoWorkerCalendar>(members);

  return items.reduce((memo, item) => {
    if (!memo.has(item.emailId)) {
      return memo;
    }

    const coWorkerCalendar = memo.get(item.emailId)!;

    if (isOffHour(item, selectedDate, startHour, endHour)) {
      coWorkerCalendar.offHourMeetings.push(item);
    } else {
      const rowIndex = getRowIndex(coWorkerCalendar.meetings, item);

      if (!coWorkerCalendar.meetings[rowIndex]) {
        coWorkerCalendar.meetings[rowIndex] = [];
      }
      coWorkerCalendar.meetings[rowIndex].push(item);
    }

    memo.set(item.emailId, coWorkerCalendar);

    return memo;
  }, memberMap);
}
